import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@material-ui/core';
import { HashLink as Link } from 'react-router-hash-link';

// CONSTANTS
import { ROUTE_PROFILE_SUBSCRIPTION_DETAIL, ROUTE_SUBSCRIPTION } from '@/constants/routes';

// UTILITY
import { updateUrl } from '@/utility/common';
import { externalLink, getExternalUrlParams } from '@/utility/analytics';
import { getSubscriptionLink } from '@/utility/content';
import { getLink } from '@/utility/routes';

// HOC
import { useGuard } from '@/hoc/Guard';

// COMPONENTS
import PackageCard from '@/components/Cards/PackageCard';
import StaticPlanCard from '@/components/Cards/StaticPlanCard';
import SubscriptionPlanCard from '@/components/Cards/SubscriptionPlanCard';

// STYLES
import { Content } from './styles';
import { Container } from '../../styles';

const LinkF = React.forwardRef((props, ref) => {
  return <Link {...props} ref={ref} />;
});

const Packages = ({
  data,
  params,
  packages,
  plans,
  unique_id,
  segmentUserId,
  appName,
  partner,
  activePlanId,
  appSlug,
  setFilters,
  hideExternal = false,
}) => {
  const [t] = useTranslation();
  const { pathname, search } = useLocation();
  const [plan_id] = useState(params.get('plan_id') || '');
  const guard = useGuard({
    iframe: false,
    partner: {
      use_subscription: true,
      use_authorizing: true,
    },
  });

  function handleVisitSite(planData) {
    const { app } = data;

    const externalUrlParams = getExternalUrlParams({ unique_id: unique_id || segmentUserId });
    const href = updateUrl(planData.button_url || planData.url, externalUrlParams);

    const properties = {
      location: 'app page',
      href,
      app_id: app.id,
      type: 'app',
      service_name: app.name,
      is_affiliate: !!app.is_affiliate,
      partner: partner?.slug,
      partner_id: partner?.id,
    };

    externalLink(properties);
  }

  function handleViewChannel(service_id) {
    const filters = {
      package: service_id.toString(),
      addons: [],
    };

    setFilters(filters);
  }

  function getLinkToSubscribe(plan) {
    return activePlanId === plan.id
      ? getLink(ROUTE_PROFILE_SUBSCRIPTION_DETAIL, { slug: appSlug })
      : getSubscriptionLink(getLink(ROUTE_SUBSCRIPTION, { slug: appSlug }), {
          plan_id: plan.id,
        });
  }

  const isSubscriptionTypePlan = plans[0]?.type === 'stripe';
  const needRenderSection = packages.length || (isSubscriptionTypePlan ? guard : true);
  const hasMorePlans = packages.length > 1 || plans.length > 1;
  const hideSubtitle = !hasMorePlans && data.app.has_live_tv;

  if (!needRenderSection) {
    return null;
  }

  return (
    <div id="packages">
      <Container className="page-content">
        <Typography className="title m-b-10" variant="h3" align="center">
          {t('appDetails.packages.title', { app_name: appName })}
        </Typography>
        {!hideSubtitle && (
          <Typography className="p3 subtitle" component="p" variant="inherit" align="center">
            {t('appDetails.packages.subtitle', { app_name: appName })}
          </Typography>
        )}
        <Content>
          <Grid container spacing={4} justifyContent="center">
            {packages.map(pack => {
              const viewChannelProps = {
                component: LinkF,
                smooth: true,
                to: `${pathname}${search}#channels`,
                onClick: () => handleViewChannel(pack.service_id),
              };

              return (
                <Grid key={pack.service_id} item xs={12} md={4}>
                  <PackageCard
                    data={pack}
                    unique_id={unique_id || segmentUserId}
                    viewChannelProps={viewChannelProps}
                    isActive={plan_id === pack.service_id}
                    handleVisitSite={() => handleVisitSite(pack)}
                    partner={partner.slug}
                    hideExternal={hideExternal}
                  />
                </Grid>
              );
            })}
            {plans.map((plan, idx) => {
              return (
                <Grid key={plan.id} item xs={12} md={4}>
                  {isSubscriptionTypePlan ? (
                    guard && (
                      <SubscriptionPlanCard
                        data={plan}
                        isActive={activePlanId === plan.id}
                        linkToSubscribe={getLinkToSubscribe(plan)}
                        trialDays={(plan.has_trial && plan.trial_days) || 0}
                        location={`Slot ${idx + 1}`}
                        partner={partner}
                        hideExternal={hideExternal}
                      />
                    )
                  ) : (
                    <StaticPlanCard
                      data={plan}
                      unique_id={unique_id || segmentUserId}
                      isActive={plan_id === plan.id}
                      handleVisitSite={() => handleVisitSite(plan)}
                      partner={partner.slug}
                      hideExternal={hideExternal}
                    />
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Content>
      </Container>
    </div>
  );
};

Packages.propTypes = {
  data: PropTypes.object.isRequired,
  packages: PropTypes.array.isRequired,
  plans: PropTypes.array.isRequired,
  unique_id: PropTypes.string,
  setFilters: PropTypes.func,
  appName: PropTypes.string.isRequired,
  params: PropTypes.object.isRequired,
  segmentUserId: PropTypes.string.isRequired,
  partner: PropTypes.object,
  activePlanId: PropTypes.number,
  appSlug: PropTypes.string.isRequired,
  hideExternal: PropTypes.bool,
};

export default Packages;
